import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const ENTITY_TO_DRAKE = {
  '1040': '1040',
  'schedule_c': '1040',
  '1120S': '1120S',
  '1065': '1065',
  '1120': '1120',
  '990': '990'
}

export default function DrakeExportPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [filterEntity, setFilterEntity] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [exporting, setExporting] = useState(false)
  const [exported, setExported] = useState(false)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('clients')
        .select('*')
        .eq('cpa_id', profile.id)
        .order('business_name', { ascending: true })
      setClients(data || [])
      // Select all by default
      setSelected(new Set((data || []).map(c => c.id)))
      setLoading(false)
    }
    load()
  }, [])

  const filtered = clients.filter(c => {
    const matchEntity = filterEntity === 'all' || c.entity_type === filterEntity
    const matchStatus = filterStatus === 'all' || c.status === filterStatus
    return matchEntity && matchStatus
  })

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filtered.map(c => c.id)))
    }
  }

  function toggleClient(id) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function exportDrakeCSV() {
    setExporting(true)
    const toExport = filtered.filter(c => selected.has(c.id))

    // Drake CSV format - matches Drake's expected import layout
    const headers = [
      'Return Type',
      'Last Name / Business Name',
      'First Name',
      'SSN/EIN',
      'Address',
      'City',
      'State',
      'ZIP',
      'Phone',
      'Email',
      'Tax Year',
      'Filing Status',
      'ClearFile ID'
    ]

    const rows = toExport.map(c => {
      const isIndividual = c.entity_type === '1040' || c.entity_type === 'schedule_c'
      const nameParts = (c.full_name || '').trim().split(' ')
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : c.full_name || ''
      const firstName = nameParts[0] || ''

      return [
        ENTITY_TO_DRAKE[c.entity_type] || '1040',
        isIndividual ? lastName : (c.business_name || c.full_name || ''),
        isIndividual ? firstName : '',
        '', // SSN/EIN - blank for security, fill in Drake
        '', // Address - fill in Drake
        '', // City
        'NC', // Default state
        '', // ZIP
        c.phone || '',
        c.email || '',
        c.tax_year || new Date().getFullYear(),
        isIndividual ? 'MFJ' : '', // Default filing status
        c.id
      ]
    })

    const csvContent = [headers, ...rows]
      .map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `Drake_Clients_${new Date().getFullYear()}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)

    setExporting(false)
    setExported(true)
    setTimeout(() => setExported(false), 5000)
  }

  function exportPaymentsCSV() {
    // Export in Drake payment import format
    // SSN, Description, Date, Amount
    const toExport = filtered.filter(c => selected.has(c.id))
    const today = new Date()
    const dateStr = `${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}${today.getFullYear()}`

    const headers = ['SSN/EIN', 'Description', 'Date', 'Amount']
    const rows = toExport.map(c => [
      '', // SSN/EIN - fill in manually
      `Tax Year ${c.tax_year || new Date().getFullYear()} Preparation Fee`,
      dateStr,
      '' // Amount - fill in manually
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(val => `"${String(val)}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `Drake_Payments_${new Date().getFullYear()}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 56 }} />)}
    </div>
  )

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 20, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>Drake Tax Export</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>
              Export your ClearFile clients in Drake-compatible CSV format.
            </p>
          </div>
          <div style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#CBB17B', fontWeight: 600 }}>
            {selected.size} of {filtered.length} selected
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20, background: '#F1E8D7', border: '1px solid #D8C18F' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#765B2C', marginBottom: 8 }}>📋 How to Import into Drake:</div>
        <div style={{ fontSize: 12, color: '#765B2C', lineHeight: 1.8 }}>
          <strong>Client List:</strong> Drake → Tools → File Maintenance → Import Data → Client Data<br />
          <strong>Payments:</strong> Drake → Tools → File Maintenance → Import Data → Return Preparation Payment Information<br />
          <span style={{ color: '#92400E' }}>⚠️ SSN/EIN fields are left blank for security — fill those in Drake manually.</span>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="input" style={{ width: 180 }} value={filterEntity} onChange={e => setFilterEntity(e.target.value)}>
          <option value="all">All Entity Types</option>
          <option value="1040">1040 — Personal</option>
          <option value="1120S">1120S — S-Corp</option>
          <option value="1065">1065 — Partnership</option>
          <option value="1120">1120 — C-Corp</option>
          <option value="schedule_c">Schedule C</option>
          <option value="990">990 — Nonprofit</option>
        </select>
        <select className="input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="review">In Review</option>
          <option value="filed">Filed</option>
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={exportPaymentsCSV} disabled={selected.size === 0}>
            💳 Export Payments CSV
          </button>
          <button className="btn btn-primary" onClick={exportDrakeCSV} disabled={selected.size === 0 || exporting}
            style={{ fontSize: 13 }}>
            {exporting ? 'Exporting…' : `⬇️ Export ${selected.size} Clients to Drake`}
          </button>
        </div>
      </div>

      {exported && (
        <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '12px 18px', marginBottom: 16, fontSize: 13, fontWeight: 600, color: '#065F46' }}>
          ✅ Drake CSV exported successfully! Open Drake → Tools → File Maintenance → Import Data to import.
        </div>
      )}

      {/* Client table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{ padding: '12px 20px', background: '#F7F3EB', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0}
            onChange={toggleAll} style={{ width: 16, height: 16, cursor: 'pointer' }} />
          <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>Client Name</div>
          <div style={{ width: 120, fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>Entity Type</div>
          <div style={{ width: 100, fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tax Year</div>
          <div style={{ width: 200, fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</div>
          <div style={{ width: 80, fontSize: 11, fontWeight: 700, color: '#62615D', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#62615D', fontSize: 13 }}>No clients found</div>
        ) : (
          filtered.map((c, i) => (
            <div key={c.id} onClick={() => toggleClient(c.id)}
              style={{
                padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 14,
                borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                background: selected.has(c.id) ? '#F0F9FF' : '#fff',
                cursor: 'pointer', transition: 'background 0.15s'
              }}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleClient(c.id)}
                onClick={e => e.stopPropagation()} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#202326' }}>{c.business_name || c.full_name}</div>
                {c.business_name && <div style={{ fontSize: 11, color: '#62615D' }}>{c.full_name}</div>}
              </div>
              <div style={{ width: 120, fontSize: 12, color: '#202326', fontWeight: 600 }}>
                {ENTITY_TO_DRAKE[c.entity_type] || c.entity_type}
              </div>
              <div style={{ width: 100, fontSize: 12, color: '#62615D' }}>{c.tax_year || new Date().getFullYear()}</div>
              <div style={{ width: 200, fontSize: 11, color: '#62615D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || '—'}</div>
              <div style={{ width: 80 }}>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                  background: c.status === 'filed' ? '#D1FAE5' : c.status === 'in_progress' ? '#DBEAFE' : '#F7F3EB',
                  color: c.status === 'filed' ? '#065F46' : c.status === 'in_progress' ? '#1E40AF' : '#62615D'
                }}>
                  {c.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
